import { takeEvery, all, throttle, fork, call } from 'redux-saga/effects'
import { releaseLock, acquireLock } from './lockSagas'
import { closeEncounter } from './encounterSagas'
import { handleParallelRequest } from './apiRequestTypes'
import { handleUserAlerts } from './alertSagas'
import {
  getAuthCodeToken,
  tokenRefresh,
  cancelTokenRefresh
} from './oauthSagas'

import {
  doLogin,
  logout,
  lockUserAccount,
  unlockUserAccount,
  verifyEmail,
  resendVerificationEmail,
  requestPasswordReset,
  passwordReset,
  verifyPassword,
  handleSuccessfulLogin
} from './authSagas'

import {
  loadGlobalAppConfigAuth,
  loadTreatmentAppConfig,
  loadLobbyLoginConfig,
  loadLobbyDashboardConfig
} from './configSagas'
import {
  startToolTipManager,
  stopToolTipManager
} from './tooltipSagas'
import {
  initAdminDisplay,
  initExtractsWindow,
  initInventoryWindow,
  initProfileWindow,
  initPatientFlagsWindow,
  initUserManagementWindow,
  initTreatmentPlanContainer,
  initAccountManagementWindow,
  initQuestionWindow,
  initQuestionnairesWindow,
  initProviderWindow,
  initBackupsWindow,
  initClickLogWindow,
  initMessagingWindow
} from './adminDisplaySagas'
import { initUserDetailsWindow } from './multiContextSagas.js'
import {
  appLocationChange,
  clearConnectionModals,
  setScreenContext
} from './helperSagas'
import { startClock } from './clockSagas'
import { throwXtractError } from './xtractErrorSagas.jsx'
import internalApiParser from './internalApiParserSagas'
import {
  initPurchaseOrderWindow,
  initImmunotherapySummaryWindow,
  initQuestionnaireReviewWindow,
  initStandardInjectionWindow,
  initNonXISInjectionsWindow,
  initInjectionGraphWindow,
  initPatientDetailsWindow,
  initQuestionnaireWindow,
  initActivateVialsWindow,
  initPrescriptionWindow,
  initOrderEntryWindow,
  initVialMixingWindow,
  initInjectionWindow,
  initInjectionListWindow,
  initInjAdjustWindow,
  initPatientDisplay,
  initSkintestWindow,
  resetInjectionData,
  initTrackingWindow,
  initDirectMessageWindow
} from './patientDisplaySagas'

import { renderPatientSearchHistory } from './widgetSagas'
import doXtractFileDownload from './doXtractFileDownload'
import { designModeClickLogger } from './clickLogSagas.js'

// single entry point to start all Sagas at once
export default function * rootSaga() {
  yield all([
    // throttle limits 1 request per Xms. in the case
    // below when the first xtract-auth@attempt-login is seen,
    // it will run doLogin, for the next 500ms it
    // will accept at most, one more 'xtract-auth@attempt-login'
    // which, if present, will execute at the end of
    // the 500ms period.
    takeEvery(
      'INIT_QUESTIONNAIRE_REVIEW_WIDGET',
      initQuestionnaireReviewWindow
    ),
    takeEvery('SHOW_RECENT_PATIENTS_WIDGET', renderPatientSearchHistory),
    takeEvery('INIT_ADMIN_DISPLAY', initAdminDisplay),
    takeEvery('INIT_BACKUPS_WIDGET', initBackupsWindow),
    takeEvery('INIT_EXTRACTS_WINDOW', initExtractsWindow),
    takeEvery('INIT_PROFILE_WINDOW', initProfileWindow),
    takeEvery('INIT_PATIENT_FLAGS_WINDOW', initPatientFlagsWindow),
    takeEvery('INIT_USER_MANAGEMENT_WINDOW', initUserManagementWindow),
    takeEvery('INIT_MESSAGING_WINDOW', initMessagingWindow),
    takeEvery('INIT_USER_DETAILS_WINDOW', initUserDetailsWindow),
    takeEvery('INIT_TREATMENT_PLAN_WINDOW', initTreatmentPlanContainer),
    takeEvery('INIT_INVENTORY_WINDOW', initInventoryWindow),
    takeEvery('INIT_QUESTION_WINDOW', initQuestionWindow),
    takeEvery('INIT_CLICKLOG_WINDOW', initClickLogWindow),
    takeEvery('INIT_PROVIDER_WINDOW', initProviderWindow),
    takeEvery('INIT_QUESTIONNAIRES_WINDOW', initQuestionnairesWindow),
    takeEvery('INIT_ORDER_ENTRY_WIDGET', initOrderEntryWindow),
    takeEvery('INIT_PURCHASE_ORDER_WIDGET', initPurchaseOrderWindow),
    takeEvery('INIT_ACCOUNT_MANAGEMENT_WINDOW', initAccountManagementWindow),
    takeEvery('INIT_STANDARD_INJECTION_WIDGET', initStandardInjectionWindow),
    takeEvery('SET_CONTEXT_WITH_MESSAGES', setScreenContext),
    takeEvery('INIT_CLOCK', startClock),
    takeEvery('THROW_XTRACT_ERROR', throwXtractError),
    takeEvery('INIT_NON_XIS_INJECTIONS_WIDGET', initNonXISInjectionsWindow),
    takeEvery('INIT_DEMOGRAPHICS_WIDGET', initImmunotherapySummaryWindow),
    takeEvery('INIT_INJECTION_GRAPH_WIDGET', initInjectionGraphWindow),
    takeEvery('INIT_PATIENT_DETAILS_WIDGET', initPatientDetailsWindow),
    takeEvery('INIT_ACTIVATE_VIALS_WIDGET', initActivateVialsWindow),
    takeEvery('INIT_QUESTIONNAIRE_WIDGET', initQuestionnaireWindow),
    takeEvery('LOAD_TREATMENT_APP_CONFIG', loadTreatmentAppConfig),
    takeEvery('INIT_PRESCRIPTION_WIDGET', initPrescriptionWindow),
    takeEvery('LOAD_LOBBY_LOGIN_CONFIG', loadLobbyLoginConfig),
    takeEvery('LOAD_LOBBY_DASHBOARD_CONFIG', loadLobbyDashboardConfig),
    takeEvery('INIT_VIAL_MIXING_WIDGET', initVialMixingWindow),
    takeEvery('INIT_INJ_ADJUST_WIDGET', initInjAdjustWindow),
    takeEvery('LOAD_GLOBAL_APP_CONFIG_AUTH', loadGlobalAppConfigAuth),
    takeEvery('INIT_INJECTION_WIDGET', initInjectionWindow),
    takeEvery('INIT_INJECTIONLIST_WIDGET', initInjectionListWindow),
    takeEvery('INIT_PATIENT_DISPLAY', initPatientDisplay),
    takeEvery('RESET_INJECTION_DATA', resetInjectionData),
    takeEvery('INIT_SKINTEST_WIDGET', initSkintestWindow),
    takeEvery('INIT_TRACKING_WIDGET', initTrackingWindow),
    takeEvery('INIT_DIRECT_MESSAGE_WINDOW', initDirectMessageWindow),
    takeEvery('SET_API_FAIL_COUNT', clearConnectionModals),
    takeEvery('*', internalApiParser),
    takeEvery('CLOSE_ENCOUNTER', closeEncounter),
    takeEvery('xtract-auth@logout', logout),
    takeEvery('xtract-auth@unlock-user-account', unlockUserAccount),
    takeEvery('xtract-auth@handle-user-alerts', handleUserAlerts),
    throttle(500, 'xtract-auth@attempt-login', doLogin),
    throttle(500, 'xtract-auth@get-auth-code-token', getAuthCodeToken),
    throttle(500, 'xtract-auth@lock-user-account', lockUserAccount),
    throttle(500, 'xtract-auth@auth-token-refresh', tokenRefresh, {
      logoutCallback: () => null
    }),
    throttle(500, 'xtract-auth@cancel-token-refresh', cancelTokenRefresh),
    throttle(500, 'xtract-auth@verify-email', verifyEmail),
    throttle(500, 'xtract-auth@resend-verify-email', resendVerificationEmail),
    throttle(500, 'xtract-auth@request-password-reset', requestPasswordReset),
    throttle(500, 'xtract-auth@password-reset', passwordReset),
    takeEvery('xtract-auth@verify-password', verifyPassword),
    takeEvery('start-tooltip-manager', startToolTipManager),
    takeEvery('stop-tooltip-manager', stopToolTipManager),
    takeEvery('ACQUIRE_LOCK', acquireLock),
    takeEvery('RELEASE_LOCK', releaseLock),
    takeEvery('APP_LOCATION_CHANGE', appLocationChange),
    takeEvery('EXECUTE_API_CALLBACK', handleCallback),
    takeEvery('XTRACT_API_CALL', handleParallelRequest),
    takeEvery('XTRACT_FILE_DOWNLOAD', handleFileDownload),
    takeEvery('xtract-auth@successful-login-event', handleSuccessfulLogin),
    takeEvery('UPDATE_DESIGN_MODE', designModeClickLogger),
  ])
}

function * handleCallback(action) {
  const { callback, args } = action
  yield call(callback, ...args)
}

function * handleFileDownload() {
  return yield fork(doXtractFileDownload, ...arguments)
}
